import { makeAutoObservable } from "mobx";
import { h, hydrate, render } from "preact";
import { RArray, } from "restaumatic-client";
import { LabelsCreator } from "restaumatic-client/src/labels/LabelRegistry";
import * as GroupToggle from "../../components/GroupToggle";
import FulfillmentSettings from "./FulfillmentSettings";
import LabelSection from "./LabelSection";
import SectionItemAvailabilityBadge from "./SectionItemAvailabilityBadge";
import SectionItemButton from "./SectionItemButton";
import SectionItemColumn from "./SectionItemColumn";
export class MenuIntegrationContext {
    constructor(params) {
        this.purchaseOrderScope = params.purchaseOrderScope;
        this.menu = params.menu;
        this.cart = params.cart;
        this.orderingHours = params.orderingHours;
        this.openProductCreator = params.openProductCreator;
        this.schedulesAvailableNow = this.menu.domain.schedules.filter((schedule) => schedule.includes(this.purchaseOrderScope.now));
        makeAutoObservable(this);
    }
    findSection(sectionId) {
        return this.menu.sections.rawFind(sectionId);
    }
    findSchedule(scheduleId) {
        return this.menu.domain.schedules.rawFind(scheduleId);
    }
    isScheduleAvailableNow(schedule) {
        return this.schedulesAvailableNow.includes(schedule.id);
    }
}
export class MenuIntegration {
    constructor() {
        this.isGroupToggleInitialized = false;
    }
    showElement(element) {
        element.classList.remove("hidden");
    }
    hideElement(element) {
        element.classList.add("hidden");
    }
    initializeGroupToggle() {
        if (!this.isGroupToggleInitialized) {
            GroupToggle.init();
            this.isGroupToggleInitialized = true;
        }
    }
    /*
     * This is a performance optimization that allows the early display of the first menu section
     * if it is not hidden by `pendolino/themes/base/partials/menu_group.liquid:43` due to visibilitySchedule
     * This fix makes menu appear to load much faster in restaurants not using visibilitySchedules (approx ~94% as of Mar 2024)
     */
    tryShowFirstMenuSectionEarly() {
        const firstSectionToggleElement = window.document
            .getElementsByClassName("js-toggle-group-list-item")
            .item(0);
        if (firstSectionToggleElement !== null &&
            !firstSectionToggleElement.classList.contains("hidden")) {
            this.initializeGroupToggle();
        }
    }
    initializeElements(context) {
        const sectionElements = new RArray(window.document.getElementsByClassName("menuv2-section")).mapOptional((sectionElement) => this.initializeSectionElement(context, sectionElement));
        // Important: this has to happen after hiding invisible sections, because
        // it chooses the first section to open.
        // Sometimes initialization could be performed sooner:
        // see MenuIntegration.tryEarlyShow()
        this.initializeGroupToggle();
        for (const [menuSection, sectionElement] of sectionElements) {
            this.initializeSectionContents(context, menuSection, sectionElement);
        }
        // it chooses the first section to open
        GroupToggle.init();
        // Initialize fulfillment settings
        this.initializeFulfillmentSettings(context);
    }
    initializeFulfillmentSettings(context) {
        var _a;
        const sectionElement = (_a = window.document.getElementById("fulfillment-settings-wrapper")) !== null && _a !== void 0 ? _a : null;
        // If menu widget is disabled in restaurant's account settings, server doesn't render the section
        if (sectionElement === null) {
            return;
        }
        const { fulfillmentFormController: controller, fulfillmentTime, orderDetails, restaurantName, nextAvailableOrderingDate, fulfillmentMethodOptionsShort, geolocation, } = context.cart;
        // TODO: Create a strategy for QrWaiter on the top-level
        if (fulfillmentTime.type === "QrWaiter" ||
            !controller.isFulfillmentEditable ||
            !orderDetails.isFulfillmentEditable) {
            sectionElement.remove();
            return;
        }
        const themesWithoutFrame = ["vesspa"];
        // TODO: Remove this after UX internal tests & Define which themes are supposed to have widget with frame
        const fulfillmentWidgetFrameFlag = localStorage.getItem("fulfillmentWidgetFrame");
        render(h(FulfillmentSettings, { restaurantName: restaurantName, orderDetails: orderDetails, methodController: controller.fulfillmentMethodController, methodOptions: fulfillmentMethodOptionsShort, timeController: controller.fulfillmentTimeController, fulfillmentTime: fulfillmentTime, nextAvailableOrderingDate: nextAvailableOrderingDate, geolocation: geolocation, 
            // isFrame={!themesWithoutFrame.includes(Skubacz.configuration.theme_name)}
            isFrame: fulfillmentWidgetFrameFlag !== "false" }), sectionElement);
    }
    initializeSectionElement(context, sectionElement) {
        const rawMenuSectionId = sectionElement.getAttribute("data-menu-section-id");
        if (rawMenuSectionId === null) {
            return null;
        }
        const menuSection = context.findSection(rawMenuSectionId);
        if (menuSection === null || !this.isSectionVisible(context, menuSection)) {
            console.log(`Menu section ${rawMenuSectionId} not available, hiding`);
            this.initializeToggleElements(rawMenuSectionId, false);
            this.hideElement(sectionElement);
            sectionElement.classList.remove("js-group-item");
            return null;
        }
        console.log(`Menu section ${rawMenuSectionId} available,`);
        this.initializeToggleElements(rawMenuSectionId, true);
        return [menuSection, sectionElement];
    }
    initializeSectionContents(context, menuSection, sectionElement) {
        for (const columnElement of sectionElement.getElementsByClassName("menuv2-column")) {
            this.initializeColumnElement(menuSection, columnElement);
        }
        for (const buttonElement of sectionElement.getElementsByClassName("menuv2-cart-button")) {
            this.initializeButtonElement(context, menuSection, buttonElement);
        }
        for (const availabilityScheduleElement of sectionElement.getElementsByClassName("menuv2-availability")) {
            this.initializeAvailabilityScheduleElement(context, availabilityScheduleElement);
        }
        if (Skubacz.configuration.menu_fulfillment_settings) {
            for (const labelElement of sectionElement.getElementsByClassName("js-item__label")) {
                this.initializeLabelElement(context, menuSection, labelElement);
            }
        }
    }
    isSectionVisible(context, menuSection) {
        return menuSection.visibilitySchedule === null
            ? true
            : context.isScheduleAvailableNow(menuSection.visibilitySchedule);
    }
    initializeToggleElements(menuSectionId, toggleElementsVisible) {
        for (const toggleElement of window.document.querySelectorAll(`.js-toggle-group-list-item[data-section-id="${menuSectionId}"]`)) {
            if (toggleElementsVisible) {
                this.showElement(toggleElement);
            }
            else {
                this.hideElement(toggleElement);
            }
        }
    }
    getColumn(menuSection, columnElement) {
        const menuSectionItemId = columnElement.getAttribute("data-menu-section-item-id");
        if (menuSectionItemId === null) {
            return null;
        }
        const menuSectionItem = menuSection.items.rawFind(menuSectionItemId);
        if (menuSectionItem === null ||
            menuSectionItem.content.type !== "SingleProduct") {
            return null;
        }
        const customParameterChoiceId = columnElement.getAttribute("data-choice-id");
        if (customParameterChoiceId === null) {
            return null;
        }
        return menuSectionItem.content.columns.rawFind(customParameterChoiceId);
    }
    initializeColumnElement(menuSection, columnElement) {
        const column = this.getColumn(menuSection, columnElement);
        if (column === null) {
            this.hideElement(columnElement); // TODO Verify this behaviour
            return;
        }
        // TODO investigate innerHTML
        columnElement.innerHTML = "";
        render(h(SectionItemColumn, { column: column }), columnElement);
    }
    initializeButtonElement(context, menuSection, buttonElement) {
        const menuSectionItemId = buttonElement.getAttribute("data-menu-section-item-id");
        const menuSectionItem = menuSectionItemId === null
            ? null
            : menuSection.items.rawFind(menuSectionItemId);
        if (menuSectionItem === null) {
            this.hideElement(buttonElement); // TODO Verify this behaviour
            return;
        }
        // TODO investigate innerHTML
        buttonElement.innerHTML = "";
        hydrate(h(SectionItemButton, { menuSectionItemContent: menuSectionItem.content, openProductCreator: (productInstanceCreator) => context.openProductCreator(productInstanceCreator) }), buttonElement);
    }
    initializeAvailabilityScheduleElement(context, availabilityScheduleElement) {
        const availabilityScheduleId = availabilityScheduleElement.getAttribute("data-availability-schedule-id");
        const availabilitySchedule = availabilityScheduleId === null
            ? null
            : context.findSchedule(availabilityScheduleId);
        if (availabilitySchedule === null ||
            context.isScheduleAvailableNow(availabilitySchedule)) {
            this.hideElement(availabilityScheduleElement);
            return;
        }
        const availableRangesNow = availabilitySchedule.schedule.rangesAround(context.purchaseOrderScope.now);
        render(h(SectionItemAvailabilityBadge, { availableRangesNow: availableRangesNow }), availabilityScheduleElement);
    }
    initializeLabelElement(context, menuSection, labelElement) {
        const menuSectionItemId = labelElement.getAttribute("data-menu-section-item-id");
        const menuSectionItem = menuSectionItemId === null
            ? null
            : menuSection.items.rawFind(menuSectionItemId);
        if (menuSectionItem === null) {
            this.hideElement(labelElement); // TODO Verify this behaviour
            return;
        }
        const availabilityScheduleId = labelElement.getAttribute("data-availability-schedule-id");
        const availabilitySchedule = availabilityScheduleId === null
            ? null
            : context.findSchedule(availabilityScheduleId);
        const labelsCreator = new LabelsCreator({
            scope: context.purchaseOrderScope,
            productInstances: menuSectionItem.content.instances,
        });
        labelElement.replaceChildren();
        render(h(LabelSection, { labelsCreator: labelsCreator, schedule: availabilitySchedule === null ? undefined : availabilitySchedule }), labelElement);
    }
}
